/**
 * <s-media-thumbs> — mobile thumbnail strip for the product gallery.
 *
 * Attributes:
 *   target — id of the horizontal scroller holding [data-media-slide] slides.
 *
 * Commands (sent with commandfor pointing at this element):
 *   --go-to — scrolls the target to the slide named by the source's data-thumb-for.
 *
 * Keeps aria-current on the thumbnail matching the slide in view.
 */
class SMediaThumbs extends HTMLElement {
  #ac;
  #scroller;

  connectedCallback() {
    this.#scroller = document.getElementById(this.getAttribute('target'));
    if (!this.#scroller) return;
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    this.addEventListener('command', (event) => {
      if (event.command !== '--go-to') return;
      const id = event.source?.dataset.thumbFor;
      const slide = this.#scroller.querySelector(`[data-media-slide="${id}"]`);
      if (!slide) return;
      const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.#scroller.scrollTo({ left: slide.offsetLeft, behavior: reduce ? 'auto' : 'smooth' });
      this.#setCurrent(id);
    }, { signal });

    let frame = 0;
    this.#scroller.addEventListener('scroll', () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => this.#syncFromScroll());
    }, { passive: true, signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #syncFromScroll() {
    const slides = this.#scroller.querySelectorAll('[data-media-slide]');
    if (!slides.length) return;
    const width = this.#scroller.clientWidth || 1;
    const index = Math.min(slides.length - 1, Math.max(0, Math.round(Math.abs(this.#scroller.scrollLeft) / width)));
    this.#setCurrent(slides[index].dataset.mediaSlide);
  }

  #setCurrent(id) {
    for (const thumb of this.querySelectorAll('[data-thumb-for]')) {
      const active = thumb.dataset.thumbFor === id;
      if (active) {
        thumb.setAttribute('aria-current', 'true');
        const strip = thumb.closest('ul');
        if (strip) strip.scrollTo({ left: thumb.parentElement.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2 });
      } else {
        thumb.removeAttribute('aria-current');
      }
    }
  }
}

if (!customElements.get('s-media-thumbs')) customElements.define('s-media-thumbs', SMediaThumbs);
